import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  IconButton,
  Tooltip,
  useMediaQuery,
  useTheme
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { apiService } from '../services/apiService';

const CampaignsList = ({ showSnackbar, onViewCampaign, onCreateNew }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [campaigns, setCampaigns] = useState([]);
  const [countries, setCountries] = useState([]);
  const [channels, setChannels] = useState([]);
  const [trackingData, setTrackingData] = useState({});
  const [countryChannels, setCountryChannels] = useState({});
  const [campaignChannels, setCampaignChannels] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [campaignToDelete, setCampaignToDelete] = useState(null);
  const [deletingCampaign, setDeletingCampaign] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [countriesRes, campaignsRes, channelsRes, trackingRes, countryChannelsRes, campaignChannelsRes] = await Promise.all([
        apiService.getCountries(),
        apiService.getCampaigns(),
        apiService.getChannels(),
        apiService.getTrackingData(),
        apiService.getCountryChannels(),
        apiService.getCampaignChannels()
      ]);

      if (countriesRes.success) setCountries(countriesRes.countries);
      if (campaignsRes.success) setCampaigns(campaignsRes.campaigns);
      if (channelsRes.success) setChannels(channelsRes.channels);
      if (trackingRes.success) {
        const trackingMap = {};
        trackingRes.tracking_data.forEach(item => {
          const key = `${item.country_code}-${item.campaign_id}-${item.channel_id}`;
          trackingMap[key] = item.implementation_percentage ?? 0;
        });
        setTrackingData(trackingMap);
      }
      
      // Procesar country_channels
      if (countryChannelsRes.success) {
        const countryChannelsMap = {};
        countryChannelsRes.country_channels.forEach(item => {
          if (!countryChannelsMap[item.country_code]) {
            countryChannelsMap[item.country_code] = new Set();
          }
          if (item.is_active) {
            countryChannelsMap[item.country_code].add(item.channel_id);
          }
        });
        const countryChannelsObj = {};
        Object.keys(countryChannelsMap).forEach(countryCode => {
          countryChannelsObj[countryCode] = Array.from(countryChannelsMap[countryCode]);
        });
        setCountryChannels(countryChannelsObj);
      }
      
      // Procesar campaign_channels
      if (campaignChannelsRes.success) {
        const campaignChannelsMap = {};
        campaignChannelsRes.campaign_channels.forEach(item => {
          if (!campaignChannelsMap[item.campaign_id]) {
            campaignChannelsMap[item.campaign_id] = new Set();
          }
          if (item.is_active) {
            campaignChannelsMap[item.campaign_id].add(item.channel_id);
          }
        });
        const campaignChannelsObj = {};
        Object.keys(campaignChannelsMap).forEach(campaignId => {
          campaignChannelsObj[campaignId] = Array.from(campaignChannelsMap[campaignId]);
        });
        setCampaignChannels(campaignChannelsObj);
      }
      
      setLoading(false);
    } catch (err) {
      setError('Error al cargar los datos');
      setLoading(false);
    }
  };

  // Países que tienen al menos una fila en tracking para esta campaña (para mostrar nombres en la card).
  const getCountriesForCampaign = (campaignId) => {
    const countryCodes = new Set();
    Object.keys(trackingData).forEach(key => {
      const [countryCode, campId] = key.split('-');
      if (campId === campaignId) countryCodes.add(countryCode);
    });
    return Array.from(countryCodes);
  };

  // Obtener canales válidos para un país y campaña
  const getValidChannels = (countryCode, campaignId) => {
    const countryChans = countryChannels[countryCode] || [];
    const campaignChans = campaignChannels[campaignId] || [];
    const validChannelIds = countryChans.filter(id => campaignChans.includes(id));
    return channels.filter(ch => validChannelIds.includes(ch.id));
  };

  // Calcular cobertura de canales para un país específico y campaña
  const getCountryCampaignCoverage = (countryCode, campaignId) => {
    const validChannels = getValidChannels(countryCode, campaignId);
    let totalChannels = validChannels.length;
    let activeChannels = 0;

    validChannels.forEach(channel => {
      const key = `${countryCode}-${campaignId}-${channel.id}`;
      if (trackingData[key] >= 50) {
        activeChannels++;
      }
    });

    return { active: activeChannels, total: totalChannels };
  };

  // Calcular cobertura: solo países asociados al toolbox (con tracking), no todos los que tienen canal válido
  const getCampaignCoverage = (campaignId) => {
    const campaignCountryCodes = getCountriesForCampaign(campaignId);
    const totalCountries = campaignCountryCodes.length;
    let countriesWithMinCoverage = 0;

    campaignCountryCodes.forEach(countryCode => {
      const coverage = getCountryCampaignCoverage(countryCode, campaignId);
      const coveragePercentage = coverage.total > 0 ? (coverage.active / coverage.total) * 100 : 0;
      if (coveragePercentage >= 50) {
        countriesWithMinCoverage++;
      }
    });

    const percentage = totalCountries > 0 ? Math.round((countriesWithMinCoverage / totalCountries) * 100) : 0;
    return { active: countriesWithMinCoverage, total: totalCountries, percentage };
  };

  // Obtener nombres de países para una campaña
  const getCountryNamesForCampaign = (campaignId) => {
    const countryCodes = getCountriesForCampaign(campaignId);
    return countryCodes.map(code => {
      const country = countries.find(c => c.code === code);
      return country ? country.name : code;
    });
  };

  // Obtener nombres de canales para una campaña
  const getChannelNamesForCampaign = (campaignId) => {
    const channelIds = campaignChannels[campaignId] || [];
    return channelIds.map(id => {
      const channel = channels.find(c => c.id === id);
      return channel ? channel.name : id;
    });
  };

  const handleDeleteCampaign = async () => {
    if (!campaignToDelete) return;
    
    try {
      setDeletingCampaign(true);
      const response = await apiService.deleteCampaign(campaignToDelete.id);
      
      if (response.success) {
        showSnackbar(response.message || 'ToolBox eliminada correctamente');
        setOpenDeleteDialog(false);
        setCampaignToDelete(null);
        // Recargar datos para actualizar la lista
        await loadData();
      }
    } catch (err) {
      showSnackbar('Error al eliminar la campaña', 'error');
    } finally {
      setDeletingCampaign(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box>
      <Box 
        display="flex" 
        flexDirection={isMobile ? 'column' : 'row'}
        justifyContent="space-between" 
        alignItems={isMobile ? 'flex-start' : 'center'} 
        sx={{ mb: { xs: 2, sm: 3 }, gap: { xs: 1, sm: 0 } }}
      >
        <Typography variant={isMobile ? 'h5' : 'h4'}>
          Todos los ToolBox
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {campaigns.length} ToolBox
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => {
            if (onCreateNew) {
              onCreateNew();
            }
          }}
          fullWidth={isMobile}
          size={isMobile ? 'medium' : 'large'}
        >
          Nuevo ToolBox
        </Button>
      </Box>

      <Grid container spacing={{ xs: 2, sm: 3 }}>
        {campaigns.map((campaign) => {
          const coverage = getCampaignCoverage(campaign.id);
          const countryNames = getCountryNamesForCampaign(campaign.id);
          const channelNames = getChannelNamesForCampaign(campaign.id);

          return (
            <Grid item xs={12} sm={6} md={4} key={campaign.id}>
              <Card 
                sx={{ 
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 6
                  }
                }}
              >
                <CardContent sx={{ flexGrow: 1, position: 'relative' }}>
                  <Tooltip title="Eliminar ToolBox">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        setCampaignToDelete(campaign);
                        setOpenDeleteDialog(true);
                      }}
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        color: 'error.main',
                        minWidth: 44,
                        minHeight: 44,
                        '&:hover': {
                          backgroundColor: 'error.light',
                          color: 'error.dark'
                        }
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2, pr: 4 }}>
                    <Typography variant="h6" component="div" sx={{ fontWeight: 'bold', flexGrow: 1 }}>
                      {campaign.name}
                    </Typography>
                    <Chip 
                      label={campaign.type} 
                      color="primary" 
                      size="small"
                      sx={{ ml: 1 }}
                    />
                  </Box>

                  {campaign.description && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {campaign.description.length > 100 
                        ? `${campaign.description.substring(0, 100)}...` 
                        : campaign.description}
                    </Typography>
                  )}

                  <Divider sx={{ my: 2 }} />

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                      Países ({countryNames.length}):
                    </Typography>
                    <Box display="flex" flexWrap="wrap" gap={0.5}>
                      {countryNames.slice(0, 5).map((name, idx) => (
                        <Chip 
                          key={idx}
                          label={name} 
                          size="small" 
                          variant="outlined"
                          sx={{ fontSize: '0.7rem' }}
                        />
                      ))}
                      {countryNames.length > 5 && (
                        <Chip 
                          label={`+${countryNames.length - 5} más`} 
                          size="small" 
                          variant="outlined"
                          sx={{ fontSize: '0.7rem' }}
                        />
                      )}
                    </Box>
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                      Canales ({channelNames.length}):
                    </Typography>
                    <Box display="flex" flexWrap="wrap" gap={0.5}>
                      {channelNames.slice(0, 4).map((name, idx) => (
                        <Chip 
                          key={idx}
                          label={name} 
                          size="small" 
                          color="secondary"
                          sx={{ fontSize: '0.7rem' }}
                        />
                      ))}
                      {channelNames.length > 4 && (
                        <Chip 
                          label={`+${channelNames.length - 4} más`} 
                          size="small" 
                          color="secondary"
                          sx={{ fontSize: '0.7rem' }}
                        />
                      )}
                    </Box>
                  </Box>

                  <Box sx={{ 
                    backgroundColor: coverage.percentage >= 50 ? '#e8f5e9' : '#ffebee',
                    p: 1.5,
                    borderRadius: 1,
                    border: `2px solid ${coverage.percentage >= 50 ? '#4caf50' : '#f44336'}`
                  }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                      Cobertura:
                    </Typography>
                    <Typography variant="h6" sx={{ 
                      fontWeight: 'bold',
                      color: coverage.percentage >= 50 ? '#2e7d32' : '#c62828'
                    }}>
                      {coverage.active}/{coverage.total} países
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {coverage.percentage}% implementados (≥50%)
                    </Typography>
                  </Box>

                  {campaign.start_date && campaign.end_date && (
                    <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #e0e0e0' }}>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(campaign.start_date).toLocaleDateString('es-ES')} - {new Date(campaign.end_date).toLocaleDateString('es-ES')}
                      </Typography>
                    </Box>
                  )}
                </CardContent>
                <CardActions sx={{ p: 2, pt: 0 }}>
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={<VisibilityIcon />}
                    onClick={() => {
                      if (onViewCampaign) {
                        onViewCampaign(campaign.id);
                      }
                    }}
                  >
                    Ver Detalles
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {campaigns.length === 0 && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            No hay ToolBox registrados
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Crea tu primera ToolBox desde el Tracker de ToolBox
          </Typography>
        </Paper>
      )}

      {/* Modal de confirmación para eliminar campaña */}
      <Dialog
        open={openDeleteDialog}
        onClose={() => !deletingCampaign && setOpenDeleteDialog(false)}
        fullScreen={isMobile}
        maxWidth={isMobile ? 'xs' : 'sm'}
        fullWidth
      >
        <DialogTitle>
          <Typography variant={isMobile ? 'subtitle1' : 'h6'}>
            Eliminar Campaña
          </Typography>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Estás seguro de que deseas eliminar el ToolBox <strong>"{campaignToDelete?.name}"</strong>?
            <br /><br />
            Esta acción ocultará el ToolBox de las vistas, pero los datos históricos se mantendrán. 
            Esta acción se puede revertir.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: { xs: 1.5, sm: 2 } }}>
          <Button 
            onClick={() => {
              setOpenDeleteDialog(false);
              setCampaignToDelete(null);
            }}
            disabled={deletingCampaign}
            fullWidth={isMobile}
            size={isMobile ? 'medium' : 'large'}
          >
            Cancelar
          </Button>
          <Button 
            onClick={handleDeleteCampaign}
            color="error"
            variant="contained"
            disabled={deletingCampaign}
            startIcon={deletingCampaign ? <CircularProgress size={20} /> : <DeleteIcon />}
            fullWidth={isMobile}
            size={isMobile ? 'medium' : 'large'}
          >
            {deletingCampaign ? 'Eliminando...' : 'Eliminar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CampaignsList;
